import { AdvanceSummary, ItemHistoryRecord } from '@skylab-shared/dataModel';
import { Dictionary, RequireAtLeastOne, UnboxCollection } from '@skylab-shared/types';
import { Calc, flat, groupBy, head, ifNaN, last, maxBy, MidpointRounding, sum, unique } from '@skylab-shared/utils';
import { CalculatedDocument, DocumentCalculationContext, DocumentCalculator, DocumentItemForDocument } from '@skylab/calculations/document';
import { applyTax, removeTax } from '../../../common/calculators/item-calculation';
import { vatRateChangedOnSaleAdvanceItem } from '../../../sale/utils/sale-advance-utils';

export type AdvanceItem = DocumentItemForDocument & { no: number; };
type GrossItem = RequireAtLeastOne<AdvanceItem, 'grossValue'>;
type VatTableItem = UnboxCollection<CalculatedDocument['vatTableItems']>;

export interface AdvanceCalculationContext extends DocumentCalculationContext {
    advanceValue?: number;
    advanceReturnValue?: number;
    calculatePerAdvance?: boolean;
    vatSummary?: AdvanceSummary;
}

const round = (precision: number) => (value: number) => Calc.round(value, precision, MidpointRounding.AwayFromZero);

export class AdvanceInvoiceDocumentGrossCalculator extends DocumentCalculator<AdvanceItem, AdvanceCalculationContext> {
    public override recalculate(items: AdvanceItem[], ctx: AdvanceCalculationContext): CalculatedDocument {
        const calculatedDocument = super.recalculate(items, ctx);

        if (items.length === 0)
            return calculatedDocument;

        if (this.vatRateChanged(ctx.vatSummary!, items)) {
            return {
                ...calculatedDocument,
                vatTableItems: this.calculateAdvanceVatTable(calculatedDocument, ctx)
            };
        }

        const withPrecision = round(ctx.precision);
        const remainingAdvance = ctx.vatSummary
            ? withPrecision(calculatedDocument.grossValue - ctx.advanceValue! - ctx.vatSummary.advance!)
            : withPrecision(calculatedDocument.grossValue - ctx.advanceValue!);
        if (remainingAdvance === 0) {
            return {
                ...calculatedDocument,
                vatTableItems: this.calculateFinalAdvanceVatTable(calculatedDocument, items, ctx)
            };
        } else if (calculatedDocument.vatTableItems.length > 0) {
            return {
                ...calculatedDocument,
                vatTableItems: this.calculateAdvanceVatTable(calculatedDocument, ctx)
            };
        }

        return calculatedDocument;
    }

    public calculateRemainingAdvance(items: AdvanceItem[], ctx: AdvanceCalculationContext): number {
        if (items.length === 0)
            return 0;

        const calculatedDocument = super.recalculate(items, ctx);

        if (this.vatRateChanged(ctx.vatSummary!, items)) {
            const virtualVatTable = this.calculateVirtualVatTable(calculatedDocument, items, ctx);
            const vatTableAfterVatRateChange = this.calculateVatTableAfterVatRateChange(calculatedDocument, items, ctx);
            return this.calculateRemaningAdvanceFromVatTable(virtualVatTable, vatTableAfterVatRateChange, ctx);
        }

        const withPrecision = round(ctx.precision);

        return ctx.vatSummary
            ? withPrecision(calculatedDocument.grossValue - ctx.advanceValue! - ctx.vatSummary.advance!)
            : withPrecision(calculatedDocument.grossValue - ctx.advanceValue!);
    }

    protected sumItems(items: GrossItem[], precision: number): VatTableItem {
        const withPrecision = round(precision);
        const [{ vatRate, vatRateId }] = items;
        const grossValue = withPrecision(items.reduce((sum, { grossValue }) => sum + grossValue, 0));
        const vatValue = withPrecision(grossValue * vatRate / (1 + vatRate));
        const netValue = withPrecision(grossValue - vatValue);
        return {
            vatRate,
            vatRateId,
            netValue,
            vatValue,
            grossValue
        };
    }

    protected vatRateChanged(advanceSummary: AdvanceSummary, documentItems: AdvanceItem[]): boolean {
        return Object.keys(advanceSummary?.itemsHistory ?? {})
            .some(no =>
                // changed in the past
                vatRateChangedOnSaleAdvanceItem(advanceSummary.itemsHistory[no])
                // or changed on the current document
                || advanceSummary.itemsHistory[no][0]?.vatRate !== documentItems.find(i => i.no === +no)?.vatRate);
    }

    private calculateAdvanceVatTable(calculatedDocument: CalculatedDocument, documentContext: AdvanceCalculationContext): VatTableItem[] {
        const referenceValue = documentContext.advanceValue || -documentContext.advanceReturnValue!;
        const withPrecision = round(documentContext.precision);

        const vatTableItems = calculatedDocument.vatTableItems
            .map(vatTableItem => {
                const grossValue = ifNaN(withPrecision(vatTableItem.grossValue / calculatedDocument.grossValue * referenceValue), 0);
                const netValue = withPrecision(removeTax(grossValue, vatTableItem.vatRate));
                const vatValue = withPrecision(grossValue - netValue);
                return { ...vatTableItem, grossValue, netValue, vatValue };
            });

        const { vatRateId: greatestValueVatTableItemId } = maxBy(calculatedDocument.vatTableItems, x => x.grossValue);
        const itemWithGreatestValueIdx = vatTableItems.findIndex(x => x.vatRateId === greatestValueVatTableItemId);
        const itemWithGreatestValue = vatTableItems[itemWithGreatestValueIdx];
        const grossSum = sum(vatTableItems.filter(x => x.vatRateId !== greatestValueVatTableItemId), x => x.grossValue);
        const grossValue = withPrecision(referenceValue - grossSum);
        const netValue = withPrecision(removeTax(grossValue, itemWithGreatestValue.vatRate));
        const vatValue = withPrecision(grossValue - netValue);
        vatTableItems[itemWithGreatestValueIdx] = { ...itemWithGreatestValue, grossValue, netValue, vatValue };

        return vatTableItems;
    }

    protected calculateFinalAdvanceVatTable(calculatedDocument: CalculatedDocument, documentItems: DocumentItemForDocument[], ctx: AdvanceCalculationContext): VatTableItem[] {
        const withPrecision = round(ctx.precision);
        return calculatedDocument.vatTableItems.map(vatTableItem => {
            const filteredDocumentItems = documentItems.filter(documentItem => documentItem.vatRateId === vatTableItem.vatRateId);
            const documentGrossSum = withPrecision(filteredDocumentItems.reduce((sum, { grossValue }) => sum + grossValue!, 0));
            const documentNetSum = withPrecision(removeTax(documentGrossSum, vatTableItem.vatRate));

            const summaryItem = ctx.vatSummary?.vatTable.items.find(z => z.vatRateId == vatTableItem.vatRateId);
            const summaryGross = summaryItem?.grossValue ?? 0;
            const summaryNet = summaryItem?.netValue ?? 0;

            const grossValue = withPrecision(documentGrossSum - summaryGross);
            const netValue = withPrecision(documentNetSum - summaryNet);
            const vatValue = withPrecision(grossValue - netValue);

            return {
                grossValue: grossValue,
                netValue: netValue,
                vatValue: vatValue,
                vatRate: vatTableItem.vatRate,
                vatRateId: vatTableItem.vatRateId
            };
        });
    }

    private calculateVirtualVatTable(calculatedDocument: CalculatedDocument, documentItems: AdvanceItem[], ctx: AdvanceCalculationContext): VatTableItem[] {
        type PriceChangeKind = 'unchanged' | 'gross' | 'net';

        const withPrecision = round(ctx.precision);
        const indexOfAdvanceWithVatRateChanged = this.findIndexOfAdvanceWithChangedVatRate(documentItems, ctx.vatSummary!.itemsHistory);
        const recalculatedItems = Object.keys(ctx.vatSummary?.itemsHistory ?? {})
            .map(no => ({
                no: +no,
                history: ctx.vatSummary!.itemsHistory[no].map(record => ({
                    ...record,
                    netAdvance: withPrecision(removeTax(record.advance, record.vatRate))
                })),
                changed: <PriceChangeKind>'unchanged'
            }))
            .map(itemHistory => {
                const currentItem = documentItems.find(i => i.no === itemHistory.no);
                const lastHistoryRecord = last(itemHistory.history);
                const participationRate = currentItem!.grossValue! / calculatedDocument.grossValue;
                // pozwalamy tylko na jedną zmianę to wystarczy porównać z ostatnim
                if (lastHistoryRecord.vatRate === currentItem!.vatRate && unique(itemHistory.history.map(r => r.vatRate)).length === 1) { // nie było zmiany stawki
                    // mergujemy ostatni rekord z bieżącą pozycją
                    const advance = withPrecision(participationRate * ctx.advanceValue!);
                    const netAdvance = withPrecision(removeTax(advance, currentItem!.vatRate));
                    return {
                        ...itemHistory,
                        history: [
                            ...itemHistory.history,
                            {
                                grossValue: currentItem!.grossValue,
                                netValue: currentItem!.netValue,
                                vatRate: currentItem!.vatRate,
                                vatRateId: currentItem!.vatRateId,
                                advance,
                                netAdvance
                            }
                        ]
                    };
                } else { // była zmiana stawki
                    const { netValue: originalNetValue } = head(itemHistory.history);
                    const changed = <PriceChangeKind>(currentItem!.netValue === originalNetValue ? 'gross' : 'net');
                    const advance = withPrecision(participationRate * ctx.advanceValue!);
                    const netAdvance = withPrecision(removeTax(advance, currentItem!.vatRate));
                    return {
                        ...itemHistory,
                        changed,
                        history: [
                            ...itemHistory.history,
                            lastHistoryRecord.vatRate === currentItem!.vatRate
                                ? {
                                    ...lastHistoryRecord,
                                    advance: withPrecision(lastHistoryRecord.advance + advance),
                                    netAdvance: withPrecision(lastHistoryRecord.netAdvance + netAdvance)
                                }
                                : {
                                    grossValue: currentItem!.grossValue,
                                    netValue: currentItem!.netValue,
                                    vatRate: currentItem!.vatRate,
                                    vatRateId: currentItem!.vatRateId,
                                    advance,
                                    netAdvance
                                }
                        ]
                    };
                }
            })
            .map(itemsHistory => {
                const lastRecord = last(itemsHistory.history);
                const prevRecords = itemsHistory.history.slice(0, indexOfAdvanceWithVatRateChanged);
                const { vatRateId } = documentItems.find(i => i.no === itemsHistory.no)!;
                const virtualItem = {
                    no: itemsHistory.no,
                    net: 0,
                    vatRate: lastRecord.vatRate,
                    vatRateId
                };
                if (itemsHistory.changed === 'gross') {
                    const net = withPrecision(lastRecord.netValue! - sum(prevRecords, h => h.netAdvance));
                    return { ...virtualItem, net };
                } else if (itemsHistory.changed === 'net') {
                    const remainingGross = lastRecord.grossValue! - sum(prevRecords, h => h.advance);
                    const net = withPrecision(removeTax(remainingGross, lastRecord.vatRate));
                    return { ...virtualItem, net };
                }
                const net = withPrecision(lastRecord.netValue! - sum(prevRecords, h => h.netAdvance));
                return { ...virtualItem, net };
            });

        return Array.from(groupBy(recalculatedItems, i => i.vatRateId), ([vatRateId, items]) => {
            const { vatRate } = last(items);
            const netValue = withPrecision(sum(items, i => i.net));
            const grossValue = withPrecision(applyTax(netValue, vatRate));
            return {
                netValue,
                grossValue,
                vatValue: withPrecision(grossValue - netValue),
                vatRate,
                vatRateId
            };
        });
    }

    private calculateVatTableAfterVatRateChange(calculatedDocument: CalculatedDocument, documentItems: AdvanceItem[], ctx: AdvanceCalculationContext): VatTableItem[] {
        const withPrecision = round(ctx.precision);
        const indexOfAdvanceWithVatRateChanged = this.findIndexOfAdvanceWithChangedVatRate(documentItems, ctx.vatSummary!.itemsHistory);
        const itemsAfterChange = Object.keys(ctx.vatSummary!.itemsHistory)
            .map(no => +no)
            .map(no => {
                const itemHistory = ctx.vatSummary!.itemsHistory[no];
                const currentItem = documentItems.find(i => i.no === no);
                const participationRate = currentItem!.grossValue! / calculatedDocument.grossValue;
                if (indexOfAdvanceWithVatRateChanged > 0 && indexOfAdvanceWithVatRateChanged < itemHistory.length) { // była zmiana stawki w przeszłości
                    const lastHistoryRecord = itemHistory.slice(indexOfAdvanceWithVatRateChanged).reduce((result, item) => ({
                        ...result,
                        advance: withPrecision(result.advance + item.advance)
                    }));
                    return {
                        ...lastHistoryRecord,
                        advance: withPrecision(lastHistoryRecord.advance + (ctx.advanceValue! * participationRate)),
                        vatRateId: currentItem!.vatRateId
                    };
                } else if (currentItem!.vatRate !== last(itemHistory).vatRate) { // zmiana stawki na bieżącym dokumencie
                    return {
                        netValue: currentItem!.netValue,
                        grossValue: currentItem!.grossValue,
                        vatRate: currentItem!.vatRate,
                        advance: withPrecision(ctx.advanceValue! * participationRate),
                        vatRateId: currentItem!.vatRateId
                    };
                }
                return {
                    netValue: currentItem!.netValue,
                    grossValue: currentItem!.grossValue,
                    vatRate: currentItem!.vatRate,
                    advance: withPrecision(ctx.advanceValue! * participationRate),
                    vatRateId: currentItem!.vatRateId
                };
            });

        const vatTableItems = Array.from(groupBy(itemsAfterChange, i => i.vatRateId), ([vatRateId, items]) => {
            const { vatRate } = last(items);
            const grossValue = withPrecision(sum(items, i => i.advance));
            const netValue = withPrecision(removeTax(grossValue, vatRate));
            return {
                grossValue,
                netValue,
                vatValue: withPrecision(grossValue - netValue),
                vatRateId,
                vatRate
            };
        });

        const itemWithGreatestValue = maxBy(vatTableItems, x => x.grossValue);
        const grossSum = sum(vatTableItems.filter(x => x !== itemWithGreatestValue), x => x.grossValue);
        const advancesAfterChange = [
            ...flat(Object
                .values(ctx.vatSummary!.itemsHistory)
                .map(history => history.slice(indexOfAdvanceWithVatRateChanged))
            ).map(i => i.advance),
            ctx.advanceValue
        ];
        const advanceSum = withPrecision(sum(advancesAfterChange, advance => advance!));
        itemWithGreatestValue.grossValue = withPrecision(advanceSum - grossSum);
        itemWithGreatestValue.netValue = withPrecision(removeTax(itemWithGreatestValue.grossValue, itemWithGreatestValue.vatRate));
        itemWithGreatestValue.vatValue = withPrecision(itemWithGreatestValue.grossValue - itemWithGreatestValue.netValue);

        return vatTableItems;
    }

    private calculateRemaningAdvanceFromVatTable(virtualVatTable: VatTableItem[], vatTableAfterVatRateChange: VatTableItem[], ctx: AdvanceCalculationContext): number {
        const withPrecision = round(ctx.precision);

        return withPrecision(sum(virtualVatTable, i => i.grossValue) - sum(vatTableAfterVatRateChange, i => i.grossValue));
    }

    private findIndexOfAdvanceWithChangedVatRate(documentItems: AdvanceItem[], itemsHistory: Dictionary<ItemHistoryRecord[]>): number {
        const vatRatesHistory = Object.keys(itemsHistory).reduce((result, no) => {
            result[no] = [
                ...itemsHistory[no].map(i => i.vatRate),
                documentItems.find(i => i.no === +no)?.vatRate!
            ];
            return result;
        }, <Dictionary<number[]>>{});
        const idx = Math.max(...Object.keys(vatRatesHistory)
            .map(no => vatRatesHistory[+no].map(vatRate => vatRate))
            .map(vatRates => vatRates.findIndex((_, idx, self) => self[idx] !== self[idx - 1] && self[idx - 1] != null)));
        return idx;
    }
}

export class AdvanceInvoiceAdjustmentDocumentGrossCalculator extends AdvanceInvoiceDocumentGrossCalculator {
    public override recalculate(items: AdvanceItem[], documentContext: AdvanceCalculationContext): CalculatedDocument {
        if (!documentContext.advanceReturnValue)
            return super.recalculate(items, documentContext);

        const vatRateChanged = this.vatRateChanged(documentContext.vatSummary!, items);

        if (vatRateChanged || documentContext.calculatePerAdvance === true) {
            const calculatedDocument = super.recalculate(items, {
                ...documentContext,
                advanceValue: -documentContext.advanceReturnValue,
                advanceReturnValue: 0
            });
            const withPrecision = round(documentContext.precision);
            return {
                ...calculatedDocument,
                vatTableItems: calculatedDocument.vatTableItems.map(item => {
                    const grossValue = withPrecision(item.grossValue);
                    const vatValue = withPrecision(item.vatValue);
                    const netValue = withPrecision(grossValue - vatValue);
                    return {
                        ...item,
                        grossValue: grossValue,
                        vatValue: vatValue,
                        netValue: netValue
                    };
                })
            };
        } else {
            const calculatedDocument = super.recalculate(items, {
                ...documentContext,
                advanceValue: documentContext.vatSummary!.advance! - documentContext.advanceReturnValue,
                advanceReturnValue: 0
            });
            const withPrecision = round(documentContext.precision);
            return {
                ...calculatedDocument,
                vatTableItems: calculatedDocument.vatTableItems.map(item => {
                    const summaryItem = documentContext.vatSummary!.vatTable.items.find(x => x.vatRateId === item.vatRateId);
                    const grossValue = withPrecision(item.grossValue - summaryItem!.grossValue);
                    const vatValue = withPrecision(item.vatValue - summaryItem!.vatValue);
                    const netValue = withPrecision(grossValue - vatValue);
                    return {
                        ...item,
                        grossValue: grossValue,
                        vatValue: vatValue,
                        netValue: netValue
                    };
                })
            };
        }
    }
}

export class AdvanceInvoiceDocumentNetCalculator extends AdvanceInvoiceDocumentGrossCalculator {
    protected override sumItems(items: GrossItem[], precision: number): VatTableItem {
        const withPrecision = round(precision);
        const [{ vatRate, vatRateId }] = items;

        const netValue = withPrecision(items.reduce((sum, { netValue }) => sum + netValue!, 0));
        const vatValue = withPrecision(netValue * vatRate);
        const grossValue = withPrecision(netValue + vatValue);
        return {
            vatRate,
            vatRateId,
            netValue,
            vatValue,
            grossValue
        };
    }

    protected override calculateFinalAdvanceVatTable(calculatedDocument: CalculatedDocument, documentItems: DocumentItemForDocument[], ctx: AdvanceCalculationContext): VatTableItem[] {
        const withPrecision = round(ctx.precision);
        return calculatedDocument.vatTableItems.map(vatTableItem => {
            const filteredDocumentItems = documentItems.filter(documentItem => documentItem.vatRateId === vatTableItem.vatRateId);
            const documentNetSum = withPrecision(filteredDocumentItems.reduce((sum, { netValue }) => sum + netValue!, 0));
            const documentVatSum = withPrecision(documentNetSum * vatTableItem.vatRate);

            const vatTableSummaryItem = ctx.vatSummary?.vatTable.items.find(z => z.vatRateId == vatTableItem.vatRateId);
            const vatSummaryNet = vatTableSummaryItem?.netValue ?? 0;
            const vatSummaryVat = vatTableSummaryItem?.vatValue ?? 0;

            const netValue = withPrecision(documentNetSum - vatSummaryNet);
            const vatValue = withPrecision(documentVatSum - vatSummaryVat);
            const grossValue = withPrecision(netValue + vatValue);

            return {
                grossValue: grossValue,
                netValue: netValue,
                vatValue: vatValue,
                vatRate: vatTableItem.vatRate,
                vatRateId: vatTableItem.vatRateId
            };
        });
    }
}

export class AdvanceInvoiceNetAdjustmentDocumentCalculator extends AdvanceInvoiceDocumentNetCalculator {
    public override recalculate(items: AdvanceItem[], documentContext: AdvanceCalculationContext): CalculatedDocument {
        if (!documentContext.advanceReturnValue)
            return super.recalculate(items, documentContext);

        const vatRateChanged = this.vatRateChanged(documentContext.vatSummary!, items);

        if (vatRateChanged || documentContext.calculatePerAdvance === true) {
            const calculatedDocument = super.recalculate(items, {
                ...documentContext,
                advanceValue: -documentContext.advanceReturnValue,
                advanceReturnValue: 0
            });
            const withPrecision = round(documentContext.precision);
            return {
                ...calculatedDocument,
                vatTableItems: calculatedDocument.vatTableItems.map(item => {
                    const netValue = withPrecision(item.netValue);
                    const vatValue = withPrecision(item.vatValue);
                    const grossValue = withPrecision(netValue + vatValue);

                    return {
                        ...item,
                        grossValue: grossValue,
                        vatValue: vatValue,
                        netValue: netValue
                    };
                })
            };
        } else {
            const calculatedDocument = super.recalculate(items, {
                ...documentContext,
                advanceValue: documentContext.vatSummary!.advance! - documentContext.advanceReturnValue,
                advanceReturnValue: 0
            });
            const withPrecision = round(documentContext.precision);
            return {
                ...calculatedDocument,
                vatTableItems: calculatedDocument.vatTableItems.map(item => {
                    const summaryItem = documentContext.vatSummary!.vatTable.items.find(x => x.vatRateId === item.vatRateId);
                    const netValue = withPrecision(item.netValue - summaryItem!.netValue);
                    const vatValue = withPrecision(item.vatValue - summaryItem!.vatValue);
                    const grossValue = withPrecision(netValue + vatValue);
                    return {
                        ...item,
                        grossValue: grossValue,
                        vatValue: vatValue,
                        netValue: netValue
                    };
                })
            };
        }
    }
}

// 1. Wyznaczenie "wirtualnej" tabelki VAT (dla całej wartości dokumentu, w oderwaniu od zaliczki)
//  a) jeśli nie było zmiany VATu to prosto - tak jak oblicza się zwykłą fakturę
//  b) jeśli była zmiana VATu świat dzieli się na dwie części
//   - przed zmianą i w tym momencie nas to nie interesuje
//   - po zmianie - tu trzeba wyznaczyć nową tabelkę wirtualną, uwzględniająca to co zapłacono wcześniej, i robi się to tak:
//   i. na każdej zaliczce przed zmianą wyliczamy zaliczki per pozycja i sumujemy (per pozycja) - mamy 2 wartości PZZn i PZZb (Poprzednio Zapłacone Zaliczki netto/brutto)
//   ii. dla każdej pozycji
//    -> jeśli zachowane było netto, wyliczamy Tymczasowa Wartość Pozostało netto TWPn = WN - PPZn
//    -> jeśli zachowane było brutto wyliczamy TWPn = (WB - PPZb) / (1 + vat)
//   iii. powyższe wyliczenia sumujemy w stawkach, doliczamy VAT i mamy wirtualną tabelkę na czas po zmianie

// 2. Rozdzielanie zaliczki na pozycje, by wyliczyć tabelkę właściwą na dokumencie
// Tu już prosto  kwotę dzielimy proporcjonalnie na tabelkę wynikłą z punku 1.a lub z 1.b.iii

// 3. Wyliczenie kwoty do zapłaty
//  a) bez zmian VATu... od naszej wirtualnej tabelki z 1.a odejmujemy prawdziwe tabelki i z sumy brutt mamy pozostało do zapłaty
//  b) jeśli zmieniany był VAT... od naszej wirtualnej tabelki z 1.b.iii odejmujemy prawdziwe tabelki z czasów od zmiany i z sumy brutt mamy pozostało do zapłaty
